import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import type { Category } from '../../data/inventory'
import { Modal } from '../kit'
import { errorText } from './shared'

/**
 * The category list, for engineers: add one, rename one (every item in it
 * moves with it, in one transaction on the server), or remove one nothing is
 * in. Counts beside each, so renaming "Motor" is visibly a change to 14 items.
 */
export function CategoriesDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient()
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => repository.categories() })
  const [name, setName] = useState('')

  const refresh = (): void => {
    for (const key of [['categories'], ['inventory'], ['inventory-item'], ['component-types'], ['stock']]) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }
  const add = useMutation({
    mutationFn: () => repository.addCategory(name),
    onSuccess: () => {
      setName('')
      refresh()
    },
  })

  return (
    <Modal title="Categories" subtitle="Items pick one of these. Renaming moves every item in it." onClose={onClose}>
      <form
        className="inv-cat-add"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) add.mutate()
        }}
      >
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New category"
          aria-label="New category"
          maxLength={40}
          data-autofocus
        />
        <button type="submit" className="primary" disabled={name.trim() === '' || add.isPending}>
          Add
        </button>
      </form>
      {add.isError ? <p className="critical">{errorText(add.error)}</p> : null}

      {categories.data && categories.data.length > 0 ? (
        <div className="panel">
          <div className="row-list">
            {categories.data.map((c) => (
              <CategoryRow key={c.id} category={c} onChanged={refresh} />
            ))}
          </div>
        </div>
      ) : categories.data ? (
        <p className="muted inv-quiet">No categories yet. Add the first one above.</p>
      ) : null}
    </Modal>
  )
}

function CategoryRow({ category, onChanged }: { category: Category; onChanged: () => void }): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(category.name)
  const rename = useMutation({
    mutationFn: () => repository.renameCategory(category.id, name),
    onSuccess: () => {
      setEditing(false)
      onChanged()
    },
  })
  const remove = useMutation({ mutationFn: () => repository.deleteCategory(category.id), onSuccess: onChanged })
  const failed = rename.error ?? remove.error

  if (editing) {
    return (
      <form
        className="row-item inv-cat-edit"
        onSubmit={(e) => {
          e.preventDefault()
          rename.mutate()
        }}
      >
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} aria-label="Category name" maxLength={40} autoFocus />
        <button type="submit" className="primary" disabled={name.trim() === '' || rename.isPending}>
          Save
        </button>
        <button type="button" onClick={() => setEditing(false)}>
          Cancel
        </button>
        {failed ? <span className="critical inv-cat-error">{errorText(failed)}</span> : null}
      </form>
    )
  }

  return (
    <div className="row-item">
      <span className="row-item__main">
        <span className="row-item__title">{category.name}</span>
        <span className="row-item__meta">
          {category.itemCount} item{category.itemCount === 1 ? '' : 's'}
        </span>
        {failed ? <span className="critical">{errorText(failed)}</span> : null}
      </span>
      <span className="row-item__side">
        <button type="button" onClick={() => setEditing(true)}>
          Rename
        </button>
        {category.itemCount === 0 ? (
          <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}>
            Remove
          </button>
        ) : null}
      </span>
    </div>
  )
}
